/**
 * @file        src/pages/seo/SeoHealth.jsx
 * @description SEO Management → SEO Health: the derived problem list for one
 *              storefront (GET /api/seo/audit) — missing or default titles and
 *              descriptions, over-long text, duplicates, items with no meta
 *              description, invalid JSON-LD, redirect loops/chains, an unset
 *              canonical host and the kill-switch.
 *
 *              The KPI cards and the table come from ONE response, and the
 *              server counts the cards from the same row list it returns, so a
 *              card can never claim a number the table does not show (§9d/§16).
 *              Every row links to the screen that fixes it.
 * @author      Kinetika Labs
 * @created     2026-10-09
 * @changelog
 *   - 2026-10-09 Claude — Initial (SEO Management).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Chip from '../../components/Chip';
import TableSkeleton from '../../components/TableSkeleton';
import { getSeoAudit } from '../../api/seo';
import { qk } from '../../api/queryKeys';
import { BrandBar, PickBrand, useSeoBrand, withBrand } from './seoShared';

const KIND_LABEL = {
  canonical_host_unset: 'Canonical host unset',
  kill_switch_on: 'Kill-switch on',
  invalid_json: 'Invalid JSON-LD',
  missing_default_og_image: 'No default social image',
  missing_description: 'No description',
  default_description: 'Default description',
  missing_title: 'Generic title',
  missing_og_image: 'No social image',
  title_too_long: 'Title too long',
  description_too_long: 'Description too long',
  duplicate_title: 'Duplicate title',
  duplicate_description: 'Duplicate description',
  item_missing_description: 'Item without description',
  redirect_self: 'Redirect to itself',
  redirect_loop: 'Redirect loop',
  redirect_chain: 'Redirect chain',
};

const SEVERITY = [
  { value: '', label: 'All' },
  { value: 'error', label: 'Errors' },
  { value: 'warning', label: 'Warnings' },
];

export default function SeoHealth() {
  const [brand, setBrand] = useSeoBrand();
  const [severity, setSeverity] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: qk.seo.audit(brand),
    queryFn: () => getSeoAudit(brand),
    enabled: Boolean(brand),
    placeholderData: keepPreviousData,
  });
  const report = data?.data;
  const rows = (report?.rows || []).filter((r) => !severity || r.severity === severity);
  const summary = report?.summary;

  return (
    <>
      <PageHead title="SEO Health" subtitle="SEO Management · what to fix first, per storefront" />
      <BrandBar brand={brand} onChange={setBrand}>
        <div className="seg" role="tablist" aria-label="Severity">
          {SEVERITY.map((s) => (
            <button key={s.value} type="button" className={severity === s.value ? 'active' : ''} onClick={() => setSeverity(s.value)}>
              {s.label}
            </button>
          ))}
        </div>
      </BrandBar>
      {!brand ? <PickBrand /> : (
        <>
          <div className="mini-kpi">
            <article className="mk glass neo"><small>Errors</small><b>{summary ? summary.errors : '—'}</b></article>
            <article className="mk glass neo"><small>Warnings</small><b>{summary ? summary.warnings : '—'}</b></article>
            <article className="mk glass neo"><small>Kinds of issue</small><b>{summary ? Object.keys(summary.byKind).length : '—'}</b></article>
          </div>
          <article className="panel glass">
            <div className="table-wrap">
              {isLoading ? <TableSkeleton /> : isError ? (
                <div className="empty"><p className="field-error">Could not run the audit.</p></div>
              ) : (
                <table className="table">
                  <thead><tr><th>Severity</th><th>Issue</th><th>Where</th><th>Detail</th><th>Fix</th></tr></thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr><td colSpan={5} className="empty">{report?.rows?.length ? 'Nothing at this severity.' : 'No issues found for this storefront.'}</td></tr>
                    ) : rows.map((r, i) => (
                      <tr key={`${r.kind}-${i}`} style={{ verticalAlign: 'middle' }}>
                        <td><Chip tone={r.severity === 'error' ? 'rej' : 'pend'}>{r.severity === 'error' ? 'Error' : 'Warning'}</Chip></td>
                        <td><b>{KIND_LABEL[r.kind] || r.kind}</b></td>
                        <td style={{ whiteSpace: 'normal', maxWidth: 240, overflowWrap: 'anywhere' }}>{r.target}</td>
                        <td style={{ whiteSpace: 'normal', maxWidth: 420, fontSize: '.8rem' }}>{r.message}</td>
                        <td><Link className="btn ghost sm" to={withBrand(r.link, brand)}>Fix →</Link></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </article>
        </>
      )}
    </>
  );
}
